import { LoggerService } from '@angular-ru/cdk/logger';
import { Injectable, OnDestroy } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { cloneDeep } from 'lodash-es';
import { map } from 'rxjs';
import { IDynamicTheme } from 'src/app/api/modules/core/components/theme/IDynamicTheme';
import { DefaultDSCustomTokens, GenerateDSCustomTokens } from 'src/app/api/modules/core/components/theme/tokens/DSCustomTokens';
import {
  DefaultMaterialColorTokens,
  GenerateMaterialColorSystemTokens,
  MaterialColorPalette,
} from 'src/app/api/modules/core/components/theme/tokens/MaterialColorTokens';
import { GenerateMaterialTypographySystemTokens } from 'src/app/api/modules/core/components/theme/tokens/MaterialTypographyTokens';
import { ElfCombineQueries } from 'src/app/util/ElfCombineQueries';
import { ElfWrite } from 'src/app/util/ElfWrite';

/**
 * There is a piece of logic relating to the theme which is custom by Drimify but
 * which requires state.
 *
 * See https://drimify.atlassian.net/browse/APP-1002
 *
 * At the time of writing, when high contrast is enabled, we should not use the
 * `--ds-button-background-color` as the background color for the button but return
 * to High Contrast palettes.
 */
interface DynamicThemeTracked {
  '--ds-button-background-color': string;
  '--ds-button-label-color': string;
}

/**
 * Adapts the JSON Interface to allow for custom state.
 */
interface DynamicThemeModel extends IDynamicTheme {
  tracked: DynamicThemeTracked;
}

/**
 * The Default State
 */
function initialState(): DynamicThemeModel {
  return {
    darkMode: false,
    highContrastMode: false,
    colors: DefaultMaterialColorTokens,
    typography: undefined,
    ds: DefaultDSCustomTokens,
    tracked: {
      '--ds-button-background-color': DefaultDSCustomTokens['--ds-button-background-color'],
      '--ds-button-label-color': DefaultDSCustomTokens['--ds-button-label-color'],
    },
  };
}

/**
 * The Store used for the Dynamic Theme.
 */
@Injectable({
  providedIn: 'root',
})
export class DynamicThemeRepository implements OnDestroy {
  /**
   * The store.
   */
  private store = createStore(
    {
      name: `dynamic-theme`,
    },
    withProps<DynamicThemeModel>(initialState()),
  );

  /**
   * Constructor
   *
   * @param loggerService - The Logger Service to log messages.
   */
  constructor(private readonly loggerService: LoggerService) {}

  ////////////////////////////////////////////////////////////////////
  // INITIALIZE
  ////////////////////////////////////////////////////////////////////

  /**
   * Configures the Theme State.
   */
  applyConfiguration(configuration?: IDynamicTheme) {
    const { darkMode, highContrastMode, colors, typography, ds: drimify } = this.store.getValue();

    let newDarkMode = darkMode;
    if (configuration?.darkMode === true || configuration?.darkMode === false) {
      newDarkMode = configuration.darkMode;
    }

    let newHighContrastMode = highContrastMode;
    if (configuration?.highContrastMode === true || configuration?.highContrastMode === false) {
      newHighContrastMode = configuration.highContrastMode;
    }

    let newColors = colors;
    if (configuration?.colors) {
      newColors = cloneDeep(configuration.colors);
    }

    let newTypography = typography;
    if (configuration?.typography) {
      newTypography = cloneDeep(configuration.typography);
    }

    if (configuration?.drimify) {
      this.loggerService.warn('The "drimify" property is deprecated. Please use "ds" instead.');
    }

    let newCustom = drimify;
    if (configuration?.ds) {
      newCustom = cloneDeep(configuration.ds);
    }

    const castedContent = configuration as Record<string, unknown>;
    const deprecatedThemeKeys: string[] = [
      'themeIsDark',
      'themePrimaryColor',
      'themeAccentColor',
      'themeWarnColor',
      'themeBodyColor',
      'themeBackgroundColor',
      'themeRadius',
      'themeFontBodyAlign',
      'themeFontHeadlineFamily',
      'themeFontHeadlineWeight',
      'themeFontHeadlineSize',
      'themeFontHeadlineColor',
      'themeFontBodyFamily',
      'themeFontBodyWeight',
      'themeFontBodySize',
      'themeFontButtonLabelColor',
      'themeFontButtonWeight',
      'themeTexturedButtonURL',
      'themeFormBackgroundOpacity',
      'themeFontPluginFamily',
      'themeFontPluginWeight',
      'themeFontPluginSize',
      'themeFontPluginColor',
      'themeFontPluginStrokeColor',
      'themeChatbotBotBackgroundColor',
      'themeChatbotBotTextColor',
      'themeChatbotHumanBackgroundColor',
      'themeChatbotHumanTextColor',
    ];
    for (const key of deprecatedThemeKeys) {
      if (key in castedContent) {
        this.loggerService.warn(`This theme property "${key}" is deprecated and should not be used.`);
      }
    }

    const colorTokens = GenerateMaterialColorSystemTokens(newColors);
    const typographyTokens = GenerateMaterialTypographySystemTokens(newTypography);
    const dsTokens = GenerateDSCustomTokens(newCustom);

    //hack to test
    // dsTokens['--ds-button-background-color'] = '#DA291C';
    // dsTokens['--ds-button-label-color'] = '#FFC72C';

    // update the store
    this.store.update(
      ElfWrite((state) => {
        state.darkMode = newDarkMode;
        state.highContrastMode = newHighContrastMode;
        state.colors = colorTokens;
        state.typography = typographyTokens;
        state.ds = dsTokens;
        state.tracked = {
          '--ds-button-background-color': dsTokens['--ds-button-background-color'],
          '--ds-button-label-color': dsTokens['--ds-button-label-color'],
        };
      }),
    );
  }

  /**
   * Toggle Dark Mode.
   */
  toggleDarkMode() {
    const { darkMode } = this.store.getValue();
    this.processDarkMode(!darkMode);
  }

  /**
   * Toggle High Contrast Mode.
   */
  toggleHighContrastMode() {
    const { highContrastMode } = this.store.getValue();
    this.processHighContrastMode(!highContrastMode);
  }

  /**
   * Set Dark Mode.
   *
   * @param darkMode - true for Dark Mode, false for Light Mode
   */
  setDarkMode(darkMode: boolean) {
    this.processDarkMode(darkMode);
  }

  /**
   * Set High Contrast Mode.
   *
   * @param highContrastMode - true for High Contrast Mode, false for Normal Mode
   */
  setHighContrastMode(highContrastMode: boolean) {
    this.processHighContrastMode(!highContrastMode);
  }

  /**
   * Process High Contrast Mode.
   *
   * @param highContrastMode - true for High Contrast Mode, false for Normal Mode
   */
  private processHighContrastMode(highContrastMode: boolean) {
    const { tracked, ds: drimify } = this.store.getValue();

    const updatedDrimifyTokens = cloneDeep(drimify);

    // if high contrast is enabled, we should return to the defaults for tracked variables
    if (highContrastMode) {
      updatedDrimifyTokens['--ds-button-background-color'] = DefaultDSCustomTokens['--ds-button-background-color'];
      updatedDrimifyTokens['--ds-button-label-color'] = DefaultDSCustomTokens['--ds-button-label-color'];
    } else {
      updatedDrimifyTokens['--ds-button-background-color'] = tracked['--ds-button-background-color'];
      updatedDrimifyTokens['--ds-button-label-color'] = tracked['--ds-button-label-color'];
    }

    this.store.update(
      ElfWrite((state) => {
        state.highContrastMode = highContrastMode;
        state.ds = updatedDrimifyTokens;
      }),
    );
  }

  /**
   * Process Dark Mode.
   *
   * @param darkMode - true for Dark Mode, false for Light Mode
   */
  private processDarkMode(darkMode: boolean) {
    this.store.update(
      ElfWrite((state) => {
        state.darkMode = darkMode;
      }),
    );
  }

  /**
   * Lifecycle Hook
   */
  ngOnDestroy() {
    this.store?.destroy();
  }

  ////////////////////////////////////////////////////////////////////
  // QUERIES
  ////////////////////////////////////////////////////////////////////

  /**
   * Is the Dark Mode enabled?
   */
  isDarkMode$ = this.store.pipe(select((state) => state.darkMode));

  /**
   * Is the High Contrast Mode enabled?
   */
  isHighContrastMode$ = this.store.pipe(select((state) => state.highContrastMode));

  /**
   * The Theme Colors
   */
  private _colors$ = this.store.pipe(select((state) => state.colors));

  /**
   * The selected Theme depends on the Dark Mode and High Contrast Mode.
   *
   * It will select the correct theme based on these modes along with the available themes.
   */
  activePalette$ = ElfCombineQueries([this.isDarkMode$, this.isHighContrastMode$, this._colors$]).pipe(
    map(([darkMode, highContrastMode, colors]) => {
      // the preferred mode based on user settings
      const preferredMode: 'light' | 'dark' | 'light-high-contrast' | 'dark-high-contrast' =
        darkMode && highContrastMode ? 'dark-high-contrast' : darkMode ? 'dark' : highContrastMode ? 'light-high-contrast' : 'light';

      // the fallback order for theme selection
      const modeFallbacks: ('dark-high-contrast' | 'dark' | 'light-high-contrast' | 'light')[] = [
        preferredMode,
        'dark-high-contrast',
        'dark',
        'light-high-contrast',
        'light',
      ];

      // selects the correct color variables
      const mode = modeFallbacks.find((m) => colors[m]) ?? 'light';

      // assigns the color based on the selected mode, ensuring fallback to 'light'
      const color: MaterialColorPalette = colors[mode] ?? colors['light'];

      // return the theme
      return color;
    }),
  );

  /**
   * The Theme Typography
   */
  private _typography$ = this.store.pipe(select((state) => state.typography));

  /**
   * The Theme Drimify
   */
  private _drimify$ = this.store.pipe(select((state) => state.ds));

  /**
   * The Data for Themes
   */
  theme$ = ElfCombineQueries([this.isDarkMode$, this.isHighContrastMode$, this.activePalette$, this._typography$, this._drimify$]).pipe(
    map(([darkMode, highContrastMode, activePalette, typography, drimify]) => {
      return { darkMode, highContrastMode, activePalette, typography, drimify };
    }),
  );
}
